import { Injectable } from '@angular/core';
import { UserProvider } from '../user/user';
import * as debug from 'debug';
import * as _ from 'lodash';
import { Capacitor, Plugins } from '@capacitor/core';
import {
  ToastController,
  LoadingController,
  Platform,
  AlertController,
} from 'ionic-angular';
import { CrudApiProvider } from '../crud-api/crud-api';
import { ConfigProvider } from '../config/config';
import { AndroidPermissions } from '@ionic-native/android-permissions';
import { NativeAccessProvider } from '../native-access/native-access';
import { ToolsProvider } from '../tools/tools';
import { Device } from '@capacitor/device';

declare let CmKit: any;

@Injectable()
export class UserVerificationProvider {
  log = debug('app:UserVerificationProvider');

  defaultVerifiedStatus = 'notVerified';

  constructor(
    public user: UserProvider,
    public crudApi: CrudApiProvider,
    private toastCtrl: ToastController,
    public platform: Platform,
    public alertCtrl: AlertController,
    public loadingCtrl: LoadingController,
    public config: ConfigProvider,
    private androidPermissions: AndroidPermissions,
    public nativeAccess: NativeAccessProvider,
    public tools: ToolsProvider
  ) {
    this.log('init UserVerificationProvider');
  }

  getVerificationStatus() {
    const maxFailedAttempts = this.config.get('jumioMaxFailAttempts') || 3;
    const userFailedAttempts =
      this.user.get('stats.failedVerificationAttempts') || 0;
    const verifiedStatus =
      this.user.get('states.verifiedStatus') || 'notVerified';

    if (verifiedStatus === 'verified') {
      return 'verified';
    } else if (verifiedStatus === 'pending') {
      return 'pending';
    } else if (
      verifiedStatus === 'failed' &&
      userFailedAttempts >= maxFailedAttempts
    ) {
      return 'failed';
    } else {
      return 'notVerified';
    }
  }

  isVerified() {
    return new Promise<Boolean>(async (resolve, reject) => {
      const verifiedStatus = this.getVerificationStatus();

      console.warn('isVerified', this.tools.isWeb, this.tools.isMobileWeb);
      if (this.tools.isMobileWeb) {
        // this.tools.showDownloadApp();
        // return resolve(false);
      }
      if (verifiedStatus === 'verified') {
        return resolve(true);
      } else {
        if (this.config.get('emailVerificationStatus')) {
          if (verifiedStatus === 'pending') {
            this.showPendingOverlay();
            return resolve(false);
          } else if (verifiedStatus === 'failed') {
            this.showFailedOverlay();
            return resolve(false);
          } else {
            await this.showGetVerifiedOverlay();
            return this.isVerified();
          }
        } else if (!this.config.get('emailVerificationStatus')) {
          this.showUnavailableOverlay();
        }
      }
    });
  }

  isRiskyAndNotVerified() {
    return new Promise<Boolean>(async (resolve, reject) => {
      const verifiedStatus = this.getVerificationStatus();
      const isRisky = this.user.get('states.isRisky');

      if (isRisky && verifiedStatus !== 'verified') {
        this.showGetVerifiedOverlay();
        return resolve(true);
      } else {
        return resolve(false);
      }
    });
  }

  showRiskyOverlay() {}

  showUnavailableOverlay() {
    return new Promise<void>((resolve, reject) => {
      CmKit.ActionSheet({
        props: {
          templates: {
            topIcon: `
              <div>
                <img style="width: 74px" src="assets/imgs/verificationFailed.png"
              </div>
            `,
            title: `
            <div style="text-align:center" class="cm-Margin--top cm-Margin--bottom">
              <div cm-font="Title / 20 Medium"
                   class="cm-Margin--bottom cm-Margin--top"
                   cm-color="Neutral / Mine Shaft">Service Unavailable</div>
              <div cm-font="Body 16 / Regular"
                   class="cm-Margin--bottom"
                   cm-color="Neutral / Mine Shaft">
              Unfortunately, the identity verification service is temporarily
              unavailable. Please check back to try again later today. We
              apologize for the inconvenience.
              </div>
            </div>
            `,
            cancel: `
            <div style="text-align: center">
              <button class="cm-Margin--top cm-Button cm-Button--1 cm-Width-1-2"
                        cm-color="Primary / CDLLife Blue">
                Done
              </button>
            </div>
            `,
          },
        },
      }).promise.then(async res => {
        this.log('res', res);
      });
    });
  }

  showVerifiedOverlay() {
    return new Promise<void>((resolve, reject) => {
      CmKit.ActionSheet({
        props: {
          templates: {
            topIcon: `
              <div>
                <img style="width: 74px" src="assets/imgs/verificationSuccess.png"
              </div>
            `,
            title: `
            <div style="text-align:center" class="cm-Margin--top cm-Margin--bottom">
              <div cm-font="Title / 20 Medium"
                   class="cm-Margin--bottom cm-Margin--top"
                   cm-color="Neutral / Mine Shaft">You're Verified!</div>
              <div cm-font="Body 16 / Regular"
                   class="cm-Margin--bottom"
                   cm-color="Neutral / Mine Shaft">
              Your account has been successfully verified. A checkmark will appear
              next to your name on your profile and content you create on the app.
              Thank you for doing your part to help keep the CDLLife community safe.
              </div>
            </div>
            `,
            cancel: `
            <div style="text-align: center">
              <button class="cm-Margin--top cm-Button cm-Button--1 cm-Width-1-2"
                        cm-color="Primary / CDLLife Blue">
                Done
              </button>
            </div>
            `,
          },
        },
      }).promise.then(async res => {
        this.log('res', res);
      });
    });
  }

  showDownloadApp() {
    this.tools.showDownloadApp();
  }

  showPendingOverlay() {
    return new Promise<void>((resolve, reject) => {
      CmKit.ActionSheet({
        props: {
          templates: {
            topIcon: `
              <div>
                <img style="width: 74px" src="assets/imgs/verificationPending.png"
              </div>
            `,
            title: `
            <div style="text-align:center" class="cm-Margin--top cm-Margin--bottom">
              <div cm-font="Title / 20 Medium"
                   class="cm-Margin--bottom"
                   cm-color="Neutral / Mine Shaft">Verification Pending</div>
              <div cm-font="Body 16 / Regular"
                   class="cm-Margin--bottom"
                   cm-color="Neutral / Mine Shaft">
                Thank you for submitting your verification information. Your verification
                is currently pending. Most verification checks are completed within 15
                minutes. You can check the status of your verifcation within your profile.
                Once complete, a checkmark will appear next to your name.
              </div>
            </div>
            `,
            cancel: `

            <div style="text-align: center">
              <button class="cm-Margin--top cm-Button cm-Button--1 cm-Width-1-2"
                        cm-color="Primary / CDLLife Blue">
                Done
              </button>
            </div>
            `,
          },
        },
      }).promise.then(async res => {
        this.log('res', res);
      });
    });
  }

  showFailedOverlay() {
    return new Promise<void>((resolve, reject) => {
      CmKit.ActionSheet({
        props: {
          templates: {
            topIcon: `
              <div>
                <img style="width: 74px" src="assets/imgs/verificationFailed.png"
              </div>
            `,
            title: `
            <div style="text-align:center" class="cm-Margin--top cm-Margin--bottom">
              <div cm-font="Title / 20 Medium"
                   class="cm-Margin--bottom"
                   cm-color="Neutral / Mine Shaft">Verification Failed</div>
              <div cm-font="Body 16 / Regular"
                   class="cm-Margin--bottom"
                   cm-color="Neutral / Mine Shaft">
              Your account could not be successfully verified at this time.
              </div>
            </div>
            `,
            cancel: `
            <div style="text-align: center">
              <button class="cm-Margin--top cm-Button cm-Button--1 cm-Width-1-2"
                        cm-color="Primary / CDLLife Blue">
                Done
              </button>
            </div>
            `,
          },
        },
      }).promise.then(async res => {
        this.log('res', res);
      });
    });
  }

  showGetVerifiedOverlay() {
    return new Promise<void>(async (resolve, reject) => {
      if (Capacitor.platform === 'android' || Capacitor.platform === 'ios') {
        // turning off jumio as of 4/24 due to minimum contract pricing
        return this.openEmailVerification();

        // if (this.config.get('emailVerificationStatus')) {
        //   await CmKit.ActionSheet({
        //     props: {
        //       templates: {
        //         topIcon: `
        //         <div>
        //           <img style="width: 74px" src="assets/imgs/verificationCheck.png"
        //         </div>
        //       `,
        //         title: `
        //       <div style="text-align:center;">
        //         <div cm-font="Title / 20 Medium"
        //              class="cm-Margin--bottom cm-Margin--top"
        //              cm-color="Neutral / Mine Shaft">
        //              Get Verified on CDLLife
        //              </div>
        //         <div cm-font="Body 16 / Regular"
        //              class="cm-Margin--bottom"
        //              cm-color="Neutral / Mine Shaft">
        //              To protect the CDLLife community from catfish and fake user profiles, some features require account verification.
        //              <br />
        //              <br />
        //              This process typically only takes ~15 seconds, and we do not permanently store any of your verification information.
        //         </div>
        //       </div>
        //       `,
        //         button: button => {
        //           return CmKit.utils.template`
        //       <div style="text-align:center" class="cm-Margin--top">
        //       <button class="cm-Button cm-Button--2"
        //         style="color: #262626; background: #F4F5F8;"
        //         @click=${() => {
        //           this.tools.logAnalyticsEvent(
        //             'trackCustom',
        //             'verification-cancelled'
        //           );
        //         }}>
        //         Cancel
        //       </button>
        //       &nbsp;
        //       &nbsp;
        //       <button class="cm-Button cm-Button--2"
        //         @click=${async () => {
        //           this.startVerification();
        //         }}
        //       cm-color="Primary / CDLLife Blue">
        //         Verify Account
        //       </button>
        //       </div>
        //       <div style="text-align:center; font-weight:500; margin-top: 24px;"
        //         cm-font="Button / 14 Regular"
        //         @click=${() => {
        //           return this.openEmailVerification();
        //         }}
        //         cm-color="Primary / CDLLife Blue">
        //         I want to upload a selfie instead
        //       </div>
        //     `;
        //         },
        //         cancel: ``,
        //       },
        //       buttons: [{}],
        //     },
        //   });
        // } else {
        //   await CmKit.ActionSheet({
        //     props: {
        //       templates: {
        //         topIcon: `
        //         <div>
        //           <img style="width: 74px" src="assets/imgs/verificationCheck.png"
        //         </div>
        //       `,
        //         title: `
        //       <div style="text-align:center;">
        //         <div cm-font="Title / 20 Medium"
        //              class="cm-Margin--bottom cm-Margin--top"
        //              cm-color="Neutral / Mine Shaft">
        //              Get Verified on CDLLife
        //              </div>
        //         <div cm-font="Body 16 / Regular"
        //              class="cm-Margin--bottom"
        //              cm-color="Neutral / Mine Shaft">
        //              To protect the CDLLife community from catfish and fake user profiles, some features require account verification.
        //              <br />
        //              <br />
        //              This process typically only takes ~15 seconds, and we do not permanently store any of your verification information.
        //         </div>
        //       </div>
        //       `,
        //         button: button => {
        //           return CmKit.utils.template`
        //       <div style="text-align:center" class="cm-Margin--top">
        //       <button class="cm-Button cm-Button--2"
        //         style="color: #262626; background: #F4F5F8;"
        //         @click=${() => {
        //           this.tools.logAnalyticsEvent(
        //             'trackCustom',
        //             'verification-cancelled'
        //           );
        //         }}>
        //         Cancel
        //       </button>
        //       &nbsp;
        //       &nbsp;
        //       <button class="cm-Button cm-Button--2"
        //         @click=${async () => {
        //           this.startVerification();
        //         }}
        //       cm-color="Primary / CDLLife Blue">
        //         Verify Account
        //       </button>
        //       </div>
        //     `;
        //         },
        //         cancel: ``,
        //       },
        //       buttons: [{}],
        //     },
        //   });
        // }
      } else {
        CmKit.ActionSheet({
          props: {
            templates: {
              topIcon: `
                <div>
                  <img style="width: 74px" src="assets/imgs/verificationFailed.png"
                </div>
              `,
              title: `
                <div style="text-align:center;" class="cm-Margin--top">
                  <div cm-font="Title / 20 Medium"
                      class="cm-Margin--bottom"
                      cm-color="Neutral / Mine Shaft">Download our mobile app to get verified!</div>
                  <div cm-font="Body 16 / Regular"
                      class="cm-Margin--bottom"
                      cm-color="Neutral / Mine Shaft">
                    To protect the CDLLife community from catfish and fake user profiles,
                    some features require account verification.
                  </div>
                  <div cm-font="Body 16 / Regular"
                      class="cm-Margin--bottom"
                      cm-color="Neutral / Mine Shaft">
                    This process typically only takes ~15 seconds, and we do not
                    permanently store any of your information.
                  </div>
                </div>
              `,
              cancel: `
              <div style="text-align: center">
              <button class="cm-Margin--top cm-Button cm-Button--1 cm-Width-1-2"
                        cm-color="Primary / CDLLife Blue">
                Done
              </button>
            </div>
              `,
            },
          },
        }).promise.then(async res => {
          this.log('res', res);
        });
      }
    });
  }

  openEmailVerification = (): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      await CmKit.ActionSheet({
        props: {
          templates: {
            title: `
            <div style="text-align: center;">
              <div font-weight: 700;"
                   cm-font="Title / 24 Semibold"
                   class="cm-Margin--bottom"
                   cm-color="Neutral / Mine Shaft">
                Verify your identity
              </div>
              <div cm-font="Body 14 / Regular"
                   class="cm-Margin--bottom"
                   cm-color="Neutral / Mine Shaft">
                You're just a few quick steps away from becoming a verified user!
              </div>
            </div>
            <hr class="cm-Divider cm-Margin--bottom" />
            `,
            button: button => {
              return CmKit.utils.template`
                <div style="text-align:center" class="cm-Margin--top">
                  <div class="cm-Margin--bottom">
                    <div style="display: flex; flex-direction: column; gap: 10px" class="cm-Margin--bottom">
                      <div style="font-weight: 500; font-size: 17px;" cm-color="Primary / CDLLife Blue">STEP 1</div>
                      <div cm-font="Body / 16 Regular" cm-color="Neutral / Mine Shaft">Write down your <span cm-font="Body / 16 Medium">username</span> and <span cm-font="Body / 16 Medium">today's date</span> on a piece of paper</div>
                    </div>
                    <div style="display: flex; flex-direction: column; gap: 10px; margin: 22px 0 12px;">
                      <div style="font-weight: 500; font-size: 17px;" cm-color="Primary / CDLLife Blue">STEP 2</div>
                      <div cm-font="Body / 16 Regular" cm-color="Neutral / Mine Shaft">Snap your selfie holding a piece of paper with the writing clearly displayed</div>
                    </div>
                    <div style="margin: 20px;">
                      <div>
                        <img style="width: 200px; margin: 12px 0;" src="assets/imgs/selfie-how-to.png"
                      </div>
                    </div>
                  </div>
                  <button
                    class="cm-Button cm-Button--1 cm-Width-1-1"
                    cm-color="Primary / CDLLife Blue"
                    @click=${() => {
                      this.openVerificationMailTo();
                    }}>
                    I'm ready to send my selfie
                  </button>
                </div>
              `;
            },
            cancel: `
              <div style="text-align: center; font-weight: 500;"
                   cm-font="Button / 14 Regular"
                   cm-color="Neutral / Manatee"
                   class="cm-Margin--bottom"
                   @click=${() => {
                     this.tools.logAnalyticsEvent(
                       'trackCustom',
                       'verification-email-canceled'
                     );
                   }}>
                Cancel
              </div>
            `,
          },
          buttons: [{}],
        },
      });
    });
  };

  openVerificationMailTo() {
    // https://noteforms.com/forms/cdllife-app-verification-pkdch5?1afa2828-a463-463e-8b09-17f3988474e5=Matt+Benson&1b112c16-8ced-4c08-9943-18e7f28bb691=username&edd95532-6f32-474d-a745-22d34c5929aa=mbenson247%40gmail.com
    const _id = _.get(this, 'user.info._id');
    const username = _.get(this, 'user.info.username');
    const firstName = _.get(this, 'user.info.firstName');
    const lastName = _.get(this, 'user.info.lastName');
    const email = _.get(this, 'user.info.email');

    const notion_fields = {
      'f06a6318-206e-41b4-83d6-1b4fcf28ea2a': _id,
      '1afa2828-a463-463e-8b09-17f3988474e5': firstName || '' + lastName || '',
      'edd95532-6f32-474d-a745-22d34c5929aa': email,
      '1b112c16-8ced-4c08-9943-18e7f28bb691': username,
    };

    const url =
      'https://noteforms.com/forms/cdllife-app-verification-pkdch5?' +
      new URLSearchParams(notion_fields).toString();

    this.tools.openExternalUrl(url);
  }

  async setPendingVerificationStatus() {
    const userId = this.user.get('_id');
    this.user.crudUpdate({
      states: {
        verifiedStatus: 'pending',
      },
    });
  }

  async resultsModal() {
    return new Promise<String>(async (resolve, reject) => {
      try {
        const userId = this.user.get('_id');

        this.log('setting user pending');

        await this.crudApi.put(`/api/jumio/${userId}/pending`, {});

        this.log(
          'done setting pending, begin progress bar with timout delay of: ',
          this.config.get('jumioTimeoutDelay')
        );

        await CmKit.ProgressBar({
          props: {
            numSteps: this.config.get('jumioTimeoutDelay') || 10,
            title: 'Verifying identity...',
          },
        }).promise;

        this.log('done with progress bar, getting user status from api');

        await this.user.forceUpdateUser();

        this.log(
          'got user5 status form api',
          this.user.get('states'),
          this.user.get('states.verifiedStatus')
        );

        return resolve(this.user.get('states.verifiedStatus'));
      } catch (e) {
        // api didn't respone, lets leave them as pending
        return resolve('pending');
      }
    });
  }

  errorInVerificationService() {
    const alert = this.toastCtrl.create({
      position: 'bottom',
      message: 'An error occurred while checking verification status.',
      cssClass: 'ConfirmRoute-toast',
      duration: 3000,
    });
    alert.present();

    this.tools.logAnalyticsEvent('trackCustom', 'verification-error');
  }
}



// WEBPACK FOOTER //
// ./src/providers/user-verification/user-verification.ts